import exifr from "exifr/dist/lite.esm.mjs";

export type PhotoCaptureMetadata = {
  latitude: number | null;
  longitude: number | null;
  takenAt: string | null;
};

const ExifDatePattern = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;
const ExifOffsetPattern = /^[+-]\d{2}:\d{2}$/;

/**
 * Reads GPS and DateTimeOriginal from the original file in one pass. Never throws.
 * Returns null when the file has neither a valid GPS pair nor a capture time.
 */
export async function readPhotoCaptureMetadata(file: Blob): Promise<PhotoCaptureMetadata | null> {
  try {
    const tags = await exifr.parse(file, { gps: true, reviveValues: false });

    let latitude: number | null = null;
    let longitude: number | null = null;
    if (isInRange(tags?.latitude, -90, 90) && isInRange(tags?.longitude, -180, 180)) {
      latitude = tags.latitude;
      longitude = tags.longitude;
    }

    const takenAt = readTakenAt(tags);
    if (latitude == null && takenAt == null) {
      return null;
    }

    return { latitude, longitude, takenAt };
  } catch {
    return null;
  }
}

function isInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

function readTakenAt(tags: { DateTimeOriginal?: unknown; OffsetTimeOriginal?: unknown; OffsetTime?: unknown } | undefined): string | null {
  const raw = typeof tags?.DateTimeOriginal === "string" ? tags.DateTimeOriginal.trim() : "";
  const match = ExifDatePattern.exec(raw);
  if (!match) {
    return null;
  }

  const clock = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}`;
  const offset = firstOffset(tags?.OffsetTimeOriginal) ?? firstOffset(tags?.OffsetTime);
  return offset ? clock + offset : `${clock}+00:00`;
}

function firstOffset(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return ExifOffsetPattern.test(trimmed) ? trimmed : null;
}
